import { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  ClipboardList, TrendingUp, Timer, Users, Coffee, ArrowUp, ArrowDown
} from 'lucide-react';
import DeepInsights from './DeepInsights';
import { pickTimeBadge, picksHrBadge, buildBewerkerMap, anonName } from './pickingHelpers';

const DashboardTab = ({ analytics, loading, selectedWorker, workers, onWorkerChange, dashRef, onHourClick, isGuest }) => {
  // Sort state for Bewerker Performance table (must be before any early return)
  const [perfSortCol, setPerfSortCol] = useState(null);
  const [perfSortDir, setPerfSortDir] = useState(null);

  if (loading || !analytics) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const wd = analytics.worker_details || [];
  const bewerkerMap = buildBewerkerMap(workers);
  const hourly = analytics.hourly_distribution || [];
  const daily = analytics.daily_distribution || [];

  const handlePerfSort = (col) => {
    if (perfSortCol !== col) { setPerfSortCol(col); setPerfSortDir('desc'); }
    else if (perfSortDir === 'desc') { setPerfSortDir('asc'); }
    else { setPerfSortCol(null); setPerfSortDir(null); }
  };

  const perfSortedWorkers = (() => {
    if (!perfSortCol || !perfSortDir) return wd;
    const getVal = (w) => {
      switch (perfSortCol) {
        case 'name': return (w.name || '').toLowerCase();
        case 'tasks': return w.total_tasks || 0;
        case 'pickTime': return w.avg_pick_time || 0;
        case 'picksHour': return w.avg_picks_per_hour || 0;
        case 'idle': return w.avg_idle_time_min || 0;
        case 'hrsDay': return w.avg_hours_per_day || 0;
        case 'days': return w.days_worked || 0;
        default: return 0;
      }
    };
    return [...wd].sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      if (typeof va === 'string') return perfSortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return perfSortDir === 'asc' ? va - vb : vb - va;
    });
  })();

  return (
    <div ref={dashRef} className="space-y-6">
      {/* Top KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="pk-total-tasks">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <ClipboardList className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{analytics.total_tasks}</p>
                <p className="text-xs text-muted-foreground">Total Tasks</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="pk-avg-pph">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{analytics.avg_picks_per_hour}</p>
                <p className="text-xs text-muted-foreground">Avg Picks/Hour</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="pk-avg-picktime">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <Timer className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{analytics.avg_pick_time}<span className="text-sm font-normal text-muted-foreground ml-1">min</span></p>
                <p className="text-xs text-muted-foreground">Avg Pick Time</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="pk-total-workers">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{analytics.total_workers}</p>
                <p className="text-xs text-muted-foreground">Workers (Bewerkers)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Worker Performance Table */}
      {wd.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-500" />
              Bewerker Performance
            </CardTitle>
            <CardDescription>Tasks completed, average pick time, picks/hour, and idle time per worker. Idle = total gaps between confirming a task and starting the next.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="worker-perf-table">
                <thead>
                  <tr className="border-b border-muted text-left">
                    {[
                      { col: 'name', label: 'Bewerker', align: 'text-left' },
                      { col: 'tasks', label: 'Tasks', align: 'text-right' },
                      { col: 'pickTime', label: 'Avg Pick Time', align: 'text-right' },
                      { col: 'picksHour', label: 'Picks/Hour', align: 'text-right' },
                      { col: 'idle', label: 'Avg Idle Time', align: 'text-right' },
                      { col: 'hrsDay', label: 'Hrs/Day', align: 'text-right' },
                      { col: 'days', label: 'Days', align: 'text-right' },
                    ].map(({ col, label, align }) => (
                      <th key={col} className={`pb-3 pr-4 ${align}`}>
                        <button
                          onClick={() => handlePerfSort(col)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-all cursor-pointer border ${perfSortCol === col ? 'bg-[#00CED1]/15 border-[#00CED1]/40 text-[#00CED1] shadow-sm' : 'bg-muted/50 border-muted-foreground/20 text-muted-foreground hover:bg-muted hover:text-foreground hover:border-muted-foreground/40'}`}
                          data-testid={`perf-sort-${col}`}
                        >
                          {label}
                          {perfSortCol === col && perfSortDir === 'desc' && <ArrowDown className="h-3.5 w-3.5" />}
                          {perfSortCol === col && perfSortDir === 'asc' && <ArrowUp className="h-3.5 w-3.5" />}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {perfSortedWorkers.map((w, i) => (
                    <tr key={w.name} className={`border-b border-muted/50 hover:bg-muted/20 cursor-pointer ${selectedWorker === w.name ? 'bg-blue-50' : ''}`}
                        onClick={() => onWorkerChange(selectedWorker === w.name ? '' : w.name)}
                        data-testid={`worker-row-${w.name}`}>
                      <td className="py-3 pr-4 font-medium">{anonName(w.name, bewerkerMap, isGuest)}</td>
                      <td className="py-3 pr-4 text-right font-mono">{w.total_tasks}</td>
                      <td className="py-3 pr-4 text-right">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${pickTimeBadge(w.avg_pick_time)}`}>
                          {w.avg_pick_time} min
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-right">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${picksHrBadge(w.avg_picks_per_hour)}`}>
                          {w.avg_picks_per_hour}/hr
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-right">
                        <span className="inline-flex items-center gap-1 text-xs">
                          <Coffee className="h-3 w-3 text-muted-foreground" />
                          {w.avg_idle_time_min} min
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-right font-mono text-sm">{w.avg_hours_per_day || 0}h</td>
                      <td className="py-3 text-right text-muted-foreground">{w.days_worked}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Hourly Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Avg Tasks per Hour (Peaks & Dips)</CardTitle>
            <CardDescription>
              {analytics.peaks?.length > 0 && (
                <span className="text-emerald-600">Peak: {analytics.peaks[0]?.hour}:00 ({analytics.peaks[0]?.count} avg tasks)</span>
              )}
              {analytics.dips?.length > 0 && (
                <span className="ml-3 text-rose-600">Low: {analytics.dips[0]?.hour}:00 ({analytics.dips[0]?.count} avg tasks)</span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {hourly.length > 0 ? (
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hourly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis dataKey="hour" tick={{ fontSize: 11 }} tickFormatter={(h) => `${h}:00`} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip labelFormatter={(h) => `${h}:00`} formatter={(v) => [v, 'Avg Tasks']} />
                    <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} className="cursor-pointer"
                      onClick={(data) => onHourClick?.(data.hour)} />
                  </BarChart>
                </ResponsiveContainer>
                <p className="text-[10px] text-muted-foreground text-center mt-1">Click a bar to see Wachtrij breakdown</p>
              </div>
            ) : <div className="h-[250px] flex items-center justify-center text-muted-foreground">No data</div>}
          </CardContent>
        </Card>

        {/* Daily Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Tasks per Day of Week</CardTitle>
          </CardHeader>
          <CardContent>
            {daily.length > 0 ? (
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => [v, 'Tasks']} />
                    <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : <div className="h-[250px] flex items-center justify-center text-muted-foreground">No data</div>}
          </CardContent>
        </Card>
      </div>

      {/* Deep insights — peak-hour heatmap, slowest start locations, slowest individual tasks */}
      <DeepInsights analytics={analytics} />
    </div>
  );
};

export default DashboardTab;
